/// <amd-module name="Widgets/HowToLinksWidget/HowToLinksWidget" />
import React = require("react");

import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation, routeUrl } from "VSS/Platform/Location";
import { UserClaims, userHasClaim } from "VSS/Platform/UserClaims";

import { Link } from "VSSUI/Link";

import { Image } from "VSSUI/Image";

import * as WidgetTelemetry from "Dashboards/WidgetCommon/WidgetTelemetry";
import { WidgetProps } from "Dashboards/WidgetContracts/WidgetProps";
import * as TfsWidgetContext from "Tfs/Widget/Context";

import { VisualTitle } from "Reporting/Visuals/VisualTitle";

import * as Resources from "Widgets/HowToLinksWidget/Resources";

/**
 * Encapsulates a single link with title, subtitle etc.
 */
export interface IHowToLink {
    title: string;
    subtitle: string;
    url: string;
    image: string;
    telemetryId: string;
}

export class HowToLinksWidget extends VssComponent<WidgetProps> {
    public static componentType = "howToLinksWidget";

    public static WorkTelemetryId = "Work";
    public static CodeTelemetryId = "Code";
    public static BuildTelemetryId = "Build";

    public static ChartUrl = "https://go.microsoft.com/fwlink/?LinkId=627167";
    public static PublicAnonLearnMore: string = "https://go.microsoft.com/fwlink/?linkid=867452";
    public static ChartTelemetryId = "Chart";

    private projectName: string;
    private teamName: string;

    constructor(props: WidgetProps, context: IVssComponentContext) {
        super(props);
        this.projectName = TfsWidgetContext.getProject(context.pageContext).name;
        this.teamName = props.teamContext.name;
    }

    public componentDidMount(): void {
        this.props.onWidgetLoaded();
        WidgetTelemetry.onWidgetLoaded(this.context.pageContext, this.props);
    }

    public render(): JSX.Element {
        return (
            <div className="how-to-links-widget">
                <VisualTitle suppressDefaultPadding text={Resources.Title} />
                <div>{this.renderContent()}</div>
            </div>
        );
    }

    public renderDescription(blurb: string): JSX.Element {
        return <p className="description">{blurb}</p>;
    }

    public renderContent(): JSX.Element {
        const isMember = userHasClaim(this.context.pageContext, UserClaims.Member);
        const isHosted = TfsWidgetContext.isHosted(this.context.pageContext);

        return isMember ? this.renderMemberView(isHosted, this.projectName) : this.renderNonMemberView();
    }

    public renderNonMemberView(): JSX.Element {
        const nonMemberImage = this.getLinkImage("howToLinksNonMember.png");
        return (
            <FPSLink
                href={HowToLinksWidget.PublicAnonLearnMore}
                target={this.props.openLinksInNewTab ? "_blank" : undefined}
                rel="noopener noreferrer"
            >
                {this.renderDescription(Resources.HowToLinks_BlurbNonMember)}
                <Image src={nonMemberImage} className="non-member-img" />
            </FPSLink>
        );
    }

    public renderMemberView(isHosted: boolean, projectName: string): JSX.Element {
        const description: string = isHosted ? Resources.HowToLinks_BlurbAzureDevOps : Resources.HowToLinks_BlurbTfs;
        const links: IHowToLink[] = this.getLinks(projectName);

        const linkControls: JSX.Element[] = links.map(link => {
            return (
                <div className="how-to-link" key={link.title}>
                    <Link
                        href={link.url}
                        target={this.props.openLinksInNewTab ? "_blank" : undefined}
                        rel="noopener noreferrer"
                        onClick={() => WidgetTelemetry.onWidgetClick(this.context.pageContext, this.props, { linkDescription: link.telemetryId })}
                    >
                        <Image src={link.image} className="img" height={45} width={50} containImage={true} alt="" />
                        <div className="how-to-link-title">{link.title}</div>
                        <div>{link.subtitle}</div>
                    </Link>
                </div>
            );
        });

        return (
            <>
                {this.renderDescription(description)}
                {linkControls}
            </>
        );
    }

    private getLinks(projectName: string): IHowToLink[] {
        return [
            {
                // Work
                title: Resources.HowToLinks_WorkTitle,
                subtitle: Resources.HowToLinks_WorkSubtitle,
                url: routeUrl(this.context.pageContext, "ms.vss-work-web.team-board-content-route", {
                    project: projectName,
                    team: this.teamName,
                    teamName: this.teamName,
                    pivot: "backlog"
                }),
                image: this.getLinkImage("howToLinksWork.png"),
                telemetryId: HowToLinksWidget.WorkTelemetryId
            },
            {
                // Code
                title: Resources.HowToLinks_CodeTitle,
                subtitle: Resources.HowToLinks_CodeSubtitle,
                url: routeUrl(this.context.pageContext, "ms.vss-code-web.files-route-git", { project: projectName }),
                image: this.getLinkImage("howToLinksCode.png"),
                telemetryId: HowToLinksWidget.CodeTelemetryId
            },
            {
                // Build
                title: Resources.HowToLinks_BuildTitle,
                subtitle: Resources.HowToLinks_BuildSubtitle,
                url: routeUrl(this.context.pageContext, "ms.vss-build-web.pipelines-hub-route", { project: projectName }),
                image: this.getLinkImage("howToLinksBuild.png"),
                telemetryId: HowToLinksWidget.BuildTelemetryId
            },
            {
                // Chart
                title: Resources.HowToLinks_ChartTitle,
                subtitle: Resources.HowToLinks_ChartSubtitle,
                url: HowToLinksWidget.ChartUrl,
                image: this.getLinkImage("howToLinksChart.png"),
                telemetryId: HowToLinksWidget.ChartTelemetryId
            }
        ];
    }

    private getLinkImage(image: string): string {
        const imagePath = `ms.vss-dashboards-web/how-to-links-widget-content/images/${image}`;
        return getAssetLocation(this.context.pageContext, imagePath);
    }
}

VssComponent.register(HowToLinksWidget.componentType, HowToLinksWidget);
